'use client';

import { useQueryClient } from '@tanstack/react-query';
import { TeamsApi } from '@up2stay/up2promo-api-client';
import {
  Button,
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from '@up2stay/up2promo-ui';
import { useState } from 'react';
import { toast } from 'sonner';

import { addTeamMemberPickerQueryKey } from '@/core/components/workspace/teams/add-team-member-dialog';
import { invalidateUserTeamMembershipQueries } from '@/core/hooks/use-user-team-membership';
import { triggerTapHaptic } from '@/core/lib/dnd-haptics';

interface RemoveTeamMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  teamId: string;
  teamName: string;
  teamMemberId: string;
  memberName: string;
  onSuccess: () => void;
  dict: {
    title: string;
    description: string;
    confirm: string;
    removing: string;
    cancel: string;
    success: string;
    error: string;
  };
}

export function RemoveTeamMemberDialog({
  open,
  onOpenChange,
  workspaceId,
  teamId,
  teamName,
  teamMemberId,
  memberName,
  onSuccess,
  dict,
}: RemoveTeamMemberDialogProps) {
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await TeamsApi.removeMember({
        path: { workspaceId, teamId, teamMemberId },
      });
      toast.success(dict.success);
      void queryClient.invalidateQueries({
        queryKey: addTeamMemberPickerQueryKey(workspaceId),
      });
      invalidateUserTeamMembershipQueries(queryClient, workspaceId);
      onOpenChange(false);
      onSuccess();
    } catch (err) {
      console.error('Failed to remove team member:', err);
      toast.error(dict.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={(next) => {
        void triggerTapHaptic();
        onOpenChange(next);
      }}
    >
      <ResponsiveDialogContent className="sm:max-w-[480px]">
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>{dict.title}</ResponsiveDialogTitle>
          <ResponsiveDialogDescription>
            {dict.description
              .replace('{name}', memberName)
              .replace('{team}', teamName)}
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>

        <ResponsiveDialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            {dict.cancel}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? dict.removing : dict.confirm}
          </Button>
        </ResponsiveDialogFooter>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
